import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import * as Yup from 'yup';
import {
  CrudMasterDetailView,
  GridView,
  Widget,
  FormInput,
  FormSubmitButton,
  FormSelect,
  CompositeView,
  MasterDetailForm,
  FormatText,
  FormTextArea,
  ButtonDelete,
  CrudContext,
  useRouter,
} from '@digital/ui';
import { Area, getAllAreas } from '@app/api/area';
import { Diretoria, getAllDiretorias } from '@app/api/diretoria';
import {
  getAllDepartamento,
  createDepartamento,
  updateDepartamento,
  deleteDepartamento,
  Departamento,
  getAllDepartamentoProAreaIdOptions,
} from '../../api/departamento';
import { Usuario, getAllUsuariosPorPerfil } from '../../api/usuario';
import { hasPermission } from '../../helpers/permissions';

const validationSchema = () => {

  return Yup.object().shape({
    nome: Yup.string()
      .required('Nome deve ser informado')
      .max(150, 'O nome deve ter no máximo 150 caracteres.'),
    sigla: Yup.string()
      .required('Sigla deve ser informada')
      .max(5, 'A sigla deve ter no máximo 5 caracteres.'),
    usuarioResponsavelId: Yup.string().required('Um usuário deve estar associado ao Departamento.'),
    descricao: Yup.string().nullable().max(2000, 'A descrição deve ter no máximo 2000 caracteres.'),
  })
};


const DepartamentoComponent = () => {
  const { history } = useRouter();
  const { selectedItem } = useContext(CrudContext);
  const [usuarios, setUsuarios] = useState<Usuario[]>();
  const [diretorias, setDiretorias] = useState<Diretoria[]>();
  const [areas, setAreas] = useState<Area[]>();
  const [departamentos, setDepartamentos] = useState(() => getAllDepartamento);

  const [selectedDiretoriaId, setSelectedDiretoriaId] = useState<string>();
  const [selectedAreaId, setSelectedAreaId] = useState<string>();
  const [departamentoSelecionado, setDepartamentoSelecionado] = useState<Departamento>();
  const [filteredAreas, setFilteredAreas] = useState<Area[]>([]);

  useEffect(() => {
    const getUsuariosData = async () => {
      const result = await getAllUsuariosPorPerfil(7);
      if (result) setUsuarios(result);
    };

    const getDiretoriasData = async () => {
      const result = await getAllDiretorias();
      if (result) setDiretorias(result);
    };

    const getAreasData = async () => {
      const result = await getAllAreas();
      if (result) {
        setAreas(result);
      }
    };

    getUsuariosData();
    getDiretoriasData();
    getAreasData();
  }, [selectedItem, departamentoSelecionado]);

  useEffect(() => {

    if (departamentoSelecionado && departamentoSelecionado.diretoriaId) {
      setSelectedAreaId('');
      const areasList = areas.filter(a => a.diretoriaId === departamentoSelecionado.diretoriaId);
      setFilteredAreas(areasList);
    } else {
      getAreasData();
      if (departamentoSelecionado && departamentoSelecionado.areaId) {
        setSelectedAreaId(departamentoSelecionado.areaId);
      }
    }
  }, [departamentoSelecionado]);

  const listarDepartamentos = async () => {
    setDepartamentos(() => Promise.resolve(undefined));
    const result = await getAllDepartamento();
    if (result) setDepartamentos(result);
  };

  const onCreateDepartamento = (departamento: Departamento) => {

    departamento.areaId = selectedAreaId || undefined;
    departamento.diretoriaId = selectedDiretoriaId || undefined;
    setDepartamentoSelecionado(departamento);
    return createDepartamento(departamento).then(resp => {
      if (resp.valid) listarDepartamentos();
      return resp;
    });
  };

  const onUpdateDepartamento = (departamento: Departamento) => {

    departamento.areaId = selectedAreaId || '';
    departamento.diretoriaId = selectedDiretoriaId || '';
    setDepartamentoSelecionado(departamento);
    return updateDepartamento(departamento).then(resp => {
      if (resp.valid) listarDepartamentos();
      return resp;
    });
  };

  const onDeleteDepartamento = (id: string) => {
    return deleteDepartamento(id).then(resp => {
      if (resp.valid) listarDepartamentos();
      history.push('/departamentos');
    });
  };

  const getAreasData = async () => {

    const result = await getAllAreas();
    if (result) setFilteredAreas(result);
  };

  const handleChangeDiretoria = (value: SelectValue) => {

    if (value) {
      setSelectedAreaId('');

      if (departamentoSelecionado != null) {
        departamentoSelecionado.areaId = '';
        departamentoSelecionado.diretoriaId = String(value) || '';
        setSelectedDiretoriaId(String(value));
      }

      if (areas && areas.length > 0) {
        const areasList = areas.filter(a => a.diretoriaId === value);
        if (areasList) setFilteredAreas(areasList);
      }
    } else {

      setSelectedDiretoriaId(undefined);
      departamentoSelecionado.diretoriaId = '';
      const vazio = [];
      setFilteredAreas(vazio);
      getAreasData();
    }
  };

  const handleChangeArea = (value: SelectValue) => {

    if (value) {
      //if (departamentoSelecionado != null) departamentoSelecionado.areaId = String(value) || '';
      setSelectedAreaId(String(value));
    }
  };

  return (
    <CrudMasterDetailView
      getAllItems={departamentos}
      createItem={hasPermission(['DIRETORIA_AREA_DEPARTAMENTO_CADASTRAR']) ? onCreateDepartamento : undefined}
      updateItem={hasPermission(['DIRETORIA_AREA_DEPARTAMENTO_EDITAR']) ? onUpdateDepartamento : undefined}
      disableNotification
      icon="business-flow_chart"
      detailTitleExtractor={item => `${item.sigla} - ${item.nome}`}
      filterPredicate={(item, value) =>
        item.nome.toLowerCase().includes(value.toLowerCase()) ||
        (item.descricao && item.descricao.toLowerCase().includes(value.toLowerCase())) ||
        item.sigla.toLowerCase().includes(value.toLowerCase())
      }
      titleExtractor={item => `${item.nome}`}
      descriptionExtractor={item => item.sigla}>
      <CrudContext.Consumer>
        {({ selectedItem }) => {
          setDepartamentoSelecionado(selectedItem);
          return (
            <GridView noform>
              <MasterDetailForm
                validationSchema={validationSchema}
              >
                <Widget
                  actions={[
                    selectedItem && hasPermission(['DIRETORIA_AREA_DEPARTAMENTO_DELETAR']) && (
                      <ButtonDelete
                        type="link"
                        confirm="Deseja realmente excluir este registro?"
                        okText="Confirmar"
                        onClick={() => onDeleteDepartamento(selectedItem.id)}
                      />
                    ),
                    hasPermission(['DIRETORIA_AREA_DEPARTAMENTO_CADASTRAR', 'DIRETORIA_AREA_DEPARTAMENTO_EDITAR']) && (
                      <FormSubmitButton type="primary">
                        <FormatText id="ui.commons.save" uppercase />
                      </FormSubmitButton>
                    ),
                  ]}>
                  <FormInput label="Nome" name="nome" span={6} />
                  <FormInput label="Sigla" name="sigla" span={6} />
                  <FormSelect
                    label="Diretoria"
                    name="diretoriaId"
                    placeholder="Selecione uma opção"
                    options={diretorias && diretorias.map(a => ({ text: a.nome, value: a.id.toString() }))}
                    span={6}
                    allowClear
                    optionFilterProp="children"
                    onChange={value => handleChangeDiretoria(value)}
                  />
                  <FormSelect
                    showSearch
                    optionFilterProp="children"
                    label="Área"
                    name="areaId"
                    placeholder="Selecione uma opção"
                    options={filteredAreas && filteredAreas.map(a => ({ text: a.nome, value: a.id.toString() }))}
                    span={6}
                    onChange={value => handleChangeArea(value)}
                    value={selectedAreaId}
                  />
                  <FormSelect
                    showSearch
                    optionFilterProp="children"
                    label="Responsavel"
                    allowClear
                    name="usuarioResponsavelId"
                    placeholder="Selecione uma opção"
                    options={usuarios && usuarios.map(a => ({ text: a.nome, value: a.id.toString() }))}
                    span={6}
                  />
                  <FormTextArea label="Descrição" name="descricao" rows={3} />
                </Widget>
              </MasterDetailForm>
            </GridView>
          );
        }}
      </CrudContext.Consumer>
    </CrudMasterDetailView>
  );
};

export default DepartamentoComponent;
